import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeadlineStats } from '../../hooks/useDeadlines.ts'
import { formatDateToLocal } from '../../utils/dateUtils'
import { formatDate } from '../../utils/formatDate'
import './DeadlineCalendar.css'

interface DeadlineCalendarProps {
  boardId: number
  onDateFilter: (date: string | null) => void
  selectedDate: string | null
}

export const DeadlineCalendar: React.FC<DeadlineCalendarProps> = ({
  boardId,
  onDateFilter,
  selectedDate,
}) => {
  const { t } = useTranslation()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [isExpanded, setIsExpanded] = useState(false)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Get first and last day of month
  const startDate = useMemo(() => {
    const date = new Date(year, month, 1)
    return date.toISOString().split('T')[0]
  }, [year, month])

  const endDate = useMemo(() => {
    const date = new Date(year, month + 1, 0)
    return date.toISOString().split('T')[0]
  }, [year, month])

  const { data: stats, isLoading } = useDeadlineStats(
    boardId,
    startDate,
    endDate
  )

  // Create calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startDayOfWeek = firstDay.getDay()

    const days: Array<{ date: Date | null; count: number }> = []

    // Add empty cells for days before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, count: 0 })
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateStr = formatDateToLocal(date)
      const stat = stats?.stats.find((s) => s.date === dateStr)
      days.push({ date, count: stat?.cardCount || 0 })
    }

    return days
  }, [year, month, stats])

  const getHeatmapColor = (count: number): string => {
    // 0 cards = kulrang (empty)
    if (count === 0) return 'var(--calendar-empty, #ebedf0)'

    // 1-2 cards = och yashil (light green)
    if (count <= 2) return 'var(--calendar-level-1, #c6e48b)'

    // 3-4 cards = o'rtacha yashil (medium green)
    if (count <= 4) return 'var(--calendar-level-2, #7bc96f)'

    // 5+ cards = to'q yashil (dark green)
    return 'var(--calendar-level-3, #239a3b)'
  }

  const handleDayClick = (date: Date | null, count: number) => {
    if (!date || count === 0) return
    const dateStr = formatDateToLocal(date)

    // Toggle filter - if same date clicked, clear filter
    if (selectedDate === dateStr) {
      onDateFilter(null)
    } else {
      onDateFilter(dateStr)
    }
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const handleToday = () => {
    setCurrentDate(new Date())
  }

  const monthName = formatDate(currentDate, 'MMMM yyyy')

  if (isLoading) {
    return (
      <div className="deadline-calendar deadline-calendar--loading">
        <div className="deadline-calendar__spinner">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <>
      <div
        className={`deadline-calendar ${isExpanded ? 'deadline-calendar--expanded' : ''}`}
      >
        <div className="deadline-calendar__header">
          {isExpanded && (
            <div className="deadline-calendar__controls">
              <button
                onClick={handlePrevMonth}
                className="deadline-calendar__nav"
              >
                ◀
              </button>
              <h3 className="deadline-calendar__title">{monthName}</h3>
              <button
                onClick={handleNextMonth}
                className="deadline-calendar__nav"
              >
                ▶
              </button>
              <button
                onClick={handleToday}
                className="deadline-calendar__today"
              >
                {t('calendar.today')}
              </button>
            </div>
          )}

          <button
            className="deadline-calendar__toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={
              isExpanded
                ? t('calendar.collapseCalendar')
                : t('calendar.expandCalendar')
            }
          >
            📅 {isExpanded ? '▼' : '▶'}
          </button>
        </div>

        {isExpanded && (
          <div className="deadline-calendar__body">
            <div className="deadline-calendar__weekdays">
              {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((day) => (
                <div key={day} className="deadline-calendar__weekday">
                  {t(`calendar.weekdays.${day}`)}
                </div>
              ))}
            </div>

            <div className="deadline-calendar__grid">
              {calendarDays.map((day, index) => {
                const dateStr = day.date ? formatDateToLocal(day.date) : null
                const isSelected = dateStr === selectedDate

                return (
                  <div
                    key={index}
                    className={`deadline-calendar__day ${
                      day.date ? 'deadline-calendar__day--active' : ''
                    } ${day.count > 0 ? 'deadline-calendar__day--has-cards' : ''} ${
                      isSelected ? 'deadline-calendar__day--selected' : ''
                    }`}
                    style={{
                      backgroundColor: day.date
                        ? getHeatmapColor(day.count)
                        : 'transparent',
                    }}
                    onClick={() => handleDayClick(day.date, day.count)}
                    title={
                      day.date && day.count > 0
                        ? `${day.date.getDate()} - ${t('calendar.cards', { count: day.count })} (${t('calendar.clickToFilter')})`
                        : day.date
                          ? `${day.date.getDate()}`
                          : ''
                    }
                  >
                    {day.date && (
                      <span className="deadline-calendar__day-number">
                        {day.date.getDate()}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="deadline-calendar__legend">
              <span className="deadline-calendar__legend-label">0</span>
              <div
                className="deadline-calendar__legend-item"
                style={{ backgroundColor: 'var(--calendar-empty, #ebedf0)' }}
                title={t('calendar.noCards')}
              />
              <span className="deadline-calendar__legend-label">1-2</span>
              <div
                className="deadline-calendar__legend-item"
                style={{ backgroundColor: 'var(--calendar-level-1, #c6e48b)' }}
                title={t('calendar.cards', { count: 2 })}
              />
              <span className="deadline-calendar__legend-label">3-4</span>
              <div
                className="deadline-calendar__legend-item"
                style={{ backgroundColor: 'var(--calendar-level-2, #7bc96f)' }}
                title={t('calendar.cards', { count: 4 })}
              />
              <span className="deadline-calendar__legend-label">5+</span>
              <div
                className="deadline-calendar__legend-item"
                style={{ backgroundColor: 'var(--calendar-level-3, #239a3b)' }}
                title={t('calendar.cards', { count: 5 })}
              />
            </div>

            {selectedDate && (
              <div className="deadline-calendar__filter-info">
                <span>
                  {t('calendar.filteringBy')}{' '}
                  {formatDate(new Date(selectedDate), 'MMM d, yyyy')}
                </span>
                <button
                  className="deadline-calendar__clear-filter"
                  onClick={() => onDateFilter(null)}
                >
                  {t('calendar.clearFilter')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
